const chapter = {
  id: 'coordination-compounds',
  slug: 'coordination-compounds',
  subject: 'chemistry',
  subjectLabel: 'Chemistry',
  exam: 'JEE Main + NEET',
  title: {
    english: 'Coordination Compounds',
        hindi: 'उपसहसंयोजन यौगिक',
  },
  estReadMinutes: 60,
  difficulty: {
    english: 'Medium-Hard',
        hindi: 'मध्यम-कठिन',
  },
  weightage: {
    english: '2–3 questions in JEE Main / NEET',
        hindi: 'जेईई मेन/एनईईटी में 2-3 प्रश्न',
  },
  syllabusTags: ['Inorganic Chemistry', 'JEE-Main', 'NEET', 'Class-12'],
  sections: [
    {
      id: 'terminology',
      title: {
        english: 'Werner Theory & Terminology',
        hindi: 'वर्नर सिद्धान्त और शब्दावली',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Werner (1898)** — central metal exhibits two valencies:',
        hindi: '**वर्नर (1898)** - केंद्रीय धातु दो संयोजकता प्रदर्शित करती है:',
            },
            {
              english: '• **Primary** = ionisable, satisfied by anions, equal to oxidation state.',
        hindi: '• **प्राथमिक** = आयनीकरण योग्य, आयनों द्वारा संतुष्ट, ऑक्सीकरण अवस्था के बराबर।',
            },
            {
              english: '• **Secondary** = non-ionisable, fixed in space (= coordination number, CN).',
        hindi: '• **माध्यमिक** = गैर-आयनीकरण योग्य, अंतरिक्ष में स्थिर (= समन्वय संख्या, सीएन)।',
            },
            {
              english: '**Coordination entity / sphere** — written in `[ ]`. e.g. $[Co(NH_3)_6]^{3+}$.',
        hindi: '**समन्वय इकाई/क्षेत्र** — `[ ]` में लिखा जाता है। जैसे $[Co(NH_3)_6]^{3+}$।',
            },
            {
              english: '**Ligand** — donor atom/group attached to central metal via dative bond.',
        hindi: '**लिगैंड** - दाता परमाणु/group डाइवेटिव बांड के माध्यम से केंद्रीय धातु से जुड़ा हुआ है।',
            },
            {
              english: 'By denticity: **monodentate** (NH₃, H₂O, Cl⁻, CN⁻, NO₂⁻), **bidentate** (en, ox²⁻, glycinate), **polydentate** (EDTA⁴⁻ — hexadentate).',
        hindi: 'डेंटिसिटी द्वारा: **मोनोडेंटेट** (NH₃, H₂O, Cl⁻, CN⁻, NO₂⁻), **bidentate** (en, ox²⁻, ग्लाइसिनेट), **पॉलीडेंटेट** (EDTA⁴⁻-हेक्साडेंटेट)।',
            },
            {
              english: '**Ambidentate** — can attach via two atoms: $-NO_2$ (N) vs $-ONO$ (O); $-SCN$ (S) vs $-NCS$ (N); $-CN$ (C) vs $-NC$ (N).',
        hindi: '**एम्बिडेंटेट** - दो परमाणुओं के माध्यम से जुड़ सकता है: $-NO_2$ (N) बनाम $-ONO$ (O); $-SCN$ (S) बनाम $-NCS$ (N); $-CN$ (सी) बनाम $-NC$ (एन)।',
            },
            {
              english: '**Chelate** — cyclic complex with poly­dentate ligand; **chelate effect** ⇒ extra stability.',
        hindi: '**चेलेट** - पॉलीडेंटेट लिगैंड के साथ चक्रीय परिसर; **चेलेट प्रभाव** ⇒ अतिरिक्त स्थिरता।',
            },
            {
              english: 'Coordination number (CN) = no. of donor atoms attached. Common: 4 (tetrahedral / square planar), 6 (octahedral).',
        hindi: 'समन्वय संख्या (सीएन) = नहीं. दाता परमाणु जुड़े हुए हैं। सामान्य: 4 (चतुष्फलकीय/वर्ग तलीय), 6 (अष्टफलकीय)।',
            },
          ],
        },
      ],
    },
    {
      id: 'iupac',
      title: {
        english: 'IUPAC Nomenclature',
        hindi: 'IUPAC नामकरण',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Cation named first (whether complex or simple).',
        hindi: 'धनायन का नाम पहले रखा गया (चाहे जटिल हो या सरल)।',
            },
            {
              english: 'Inside [ ], ligands named **alphabetically** (ignoring di-, tri- prefixes).',
        hindi: '[ ] के अंदर, लिगेंड्स को **वर्णानुक्रम में** नाम दिया गया है (di-, tri- उपसर्गों को नजरअंदाज करते हुए)।',
            },
            {
              english: 'Anionic ligands end in `-o`: chloro Cl⁻, hydroxo OH⁻, oxo O²⁻, cyano CN⁻, nitro NO₂⁻, sulphato SO₄²⁻.',
        hindi: 'आयनिक लिगैंड `-o` में समाप्त होते हैं: क्लोरो Cl⁻, हाइड्रॉक्सो OH⁻, ऑक्सो O²⁻, सायनो CN⁻, नाइट्रो NO₂⁻, सल्फाटो SO₄²⁻।',
            },
            {
              english: 'Neutral: ammine NH₃, aqua H₂O, carbonyl CO, nitrosyl NO.',
        hindi: 'तटस्थ: अमाइन NH₃, एक्वा H₂O, कार्बोनिल CO, नाइट्रोसिल NO।',
            },
            {
              english: 'Use di-, tri- for simple ligands; bis-, tris-, tetrakis- when ligand name itself has Greek prefix (e.g. **bis(ethylenediamine)**).',
        hindi: 'सरल लिगेंड्स के लिए di-, tri- का उपयोग करें; बीआईएस-, ट्रिस-, टेट्राकिस- जब लिगैंड नाम में ग्रीक उपसर्ग होता है (उदाहरण के लिए **बीआईएस(एथिलीनडायमाइन)**)। फिर **धातु** + रोमन अंक O.S लिखें। कोष्ठक में. यदि कॉम्प्लेक्स **आयनिक** है, तो धातु का नाम **-एट** (कप्रेट, फेर्रेट, अर्जेंटेट, ऑरेट, प्लंबेट, स्टैनेट) में समाप्त होता है।',
            },
            {
              english: 'Then write **metal** + Roman numeral O.S. in brackets. If complex is **anionic**, metal name ends in **-ate** (cuprate, ferrate, argentate, aurate, plumbate, stannate).',
        hindi: 'फिर **धातु** + रोमन अंक O.S लिखें। कोष्ठक में. यदि कॉम्प्लेक्स **आयनिक** है, तो धातु का नाम **-एट** (कप्रेट, फेर्रेट, अर्जेंटेट, ऑरेट, प्लंबेट, स्टैनेट) में समाप्त होता है।',
            },
            {
              english: 'Examples:',
        hindi: 'उदाहरण:',
            },
            {
              english: '• $[Co(NH_3)_6]Cl_3$ → hexaamminecobalt(III) chloride.',
        hindi: '• $[Co(NH_3)_6]Cl_3$ → हेक्साअमाइनकोबाल्ट(III) क्लोराइड।',
            },
            {
              english: '• $K_4[Fe(CN)_6]$ → potassium hexacyanidoferrate(II).',
        hindi: '• $K_4[Fe(CN)_6]$ → पोटैशियम हेक्सासायनिडोफेरेट(II)।',
            },
            {
              english: '• $[Pt(NH_3)_2Cl_2]$ → diamminedichloridoplatinum(II).',
        hindi: '• $[Pt(NH_3)_2Cl_2]$ → डायमिनेडाइक्लोरिडोप्लाटिनम(II)।',
            },
            {
              english: '• $[Cr(en)_3]^{3+}$ → tris(ethylenediamine)chromium(III) ion.',
        hindi: '• $[Cr(en)_3]^{3+}$ → ट्रिस(एथिलीनडायमाइन)क्रोमियम(III) आयन। **संरचनात्मक** समरूपता:',
            },
          ],
        },
      ],
    },
    {
      id: 'isomerism',
      title: {
        english: 'Isomerism',
        hindi: 'समावयवता',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Structural** isomerism:',
        hindi: '**संरचनात्मक** समरूपता:',
            },
            {
              english: '• **Ionisation** — $[Co(NH_3)_5SO_4]Br$ vs $[Co(NH_3)_5Br]SO_4$.',
        hindi: '• **आयनिकरण** — $[Co(NH_3)_5SO_4]Br$ बनाम $[Co(NH_3)_5Br]SO_4$।',
            },
            {
              english: '• **Hydrate / solvate** — $[Cr(H_2O)_6]Cl_3$, $[Cr(H_2O)_5Cl]Cl_2 \\cdot H_2O$, $[Cr(H_2O)_4Cl_2]Cl \\cdot 2H_2O$.',
        hindi: '• **हाइड्रेट/सॉल्वेट** — $[Cr(H_2O)_6]Cl_3$, $[Cr(H_2O)_5Cl]Cl_2 \\cdot H_2O$, $[Cr(H_2O)_4Cl_2]Cl \\cdot 2H_2O$।',
            },
            {
              english: '• **Linkage** — ambidentate ligand: $[Co(NH_3)_5(NO_2)]^{2+}$ (nitro) vs $[Co(NH_3)_5(ONO)]^{2+}$ (nitrito).',
        hindi: '• **लिंकेज** — एम्बिडेंटेट लिगैंड: $[Co(NH_3)_5(NO_2)]^{2+}$ (नाइट्रो) बनाम $[Co(NH_3)_5(ONO)]^{2+}$ (नाइट्रिटो)।',
            },
            {
              english: '• **Coordination** — exchange of ligands between cation & anion: $[Co(NH_3)_6][Cr(CN)_6]$ vs $[Cr(NH_3)_6][Co(CN)_6]$.',
        hindi: '• **समन्वय** — धनायन और ऋणायन के बीच लिगेंड्स का आदान-प्रदान: $[Co(NH_3)_6][Cr(CN)_6]$ बनाम $[Cr(NH_3)_6][Co(CN)_6]$। **स्टीरियोआइसोमेरिज्म**:',
            },
            {
              english: '**Stereoisomerism**:',
        hindi: '**स्टीरियोआइसोमेरिज्म**:',
            },
            {
              english: '• **Geometrical** (cis-trans, fac-mer) — square planar $MA_2B_2$ has cis & trans; $MA_3B_3$ octahedral has fac & mer.',
        hindi: '• **ज्यामितीय** (सीआईएस-ट्रांस, एफएक्स-मेर) - वर्ग तलीय $MA_2B_2$ में सीआईएस और ट्रांस है; $MA_3B_3$ अष्टफलकीय में फलक एवं मेर है।',
            },
            {
              english: '• **Optical** — chiral complexes lacking plane of symmetry; $[Co(en)_3]^{3+}$ has $\\Delta$ and $\\Lambda$ enantiomers; cis-$[CoCl_2(en)_2]^+$ is optically active, trans is not.',
        hindi: '• **ऑप्टिकल** - चिरल कॉम्प्लेक्स में समरूपता के तल का अभाव है; $[Co(en)_3]^{3+}$ में $\\Delta$ और $\\Lambda$ एनैन्टीओमर्स हैं; Cis-$[CoCl_2(en)_2]^+$ प्रकाशिक रूप से सक्रिय है, ट्रांस नहीं है।',
            },
          ],
        },
      ],
    },
    {
      id: 'vbt',
      title: {
        english: 'Valence Bond Theory (VBT)',
        hindi: 'संयोजकता बन्ध सिद्धान्त (VBT)',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Metal undergoes hybridisation; ligand donates lone pair to empty hybrid orbitals.',
        hindi: 'धातु संकरण से गुजरती है; लिगैंड खाली हाइब्रिड ऑर्बिटल्स को एकाकी जोड़ा दान करता है।',
            },
            {
              english: 'Common hybridisations:',
        hindi: 'सामान्य संकरण:',
            },
            {
              english: '• **CN 4 tetrahedral** — $sp^3$ (e.g. $[NiCl_4]^{2-}$, $[Ni(CO)_4]$).',
        hindi: '• **CN 4 चतुष्फलकीय** — $sp^3$ (उदा. $[NiCl_4]^{2-}$, $[Ni(CO)_4]$)।',
            },
            {
              english: '• **CN 4 square planar** — $dsp^2$ (e.g. $[Ni(CN)_4]^{2-}$, $[PtCl_4]^{2-}$).',
        hindi: '• **CN 4 वर्ग समतल** — $dsp^2$ (उदा. $[Ni(CN)_4]^{2-}$, $[PtCl_4]^{2-}$).',
            },
            {
              english: '• **CN 6 octahedral** — $d^2sp^3$ (inner orbital, low spin) or $sp^3d^2$ (outer orbital, high spin).',
        hindi: '• **CN 6 अष्टफलकीय** — $d^2sp^3$ (आंतरिक कक्षीय, कम स्पिन) या $sp^3d^2$ (बाहरी कक्षीय, उच्च स्पिन)।',
            },
            {
              english: 'Strong field ligand (CN⁻, CO, NH₃ for some) ⇒ pairing ⇒ **inner-orbital, low-spin**.',
        hindi: 'मजबूत क्षेत्र लिगैंड (कुछ के लिए CN⁻, CO, NH₃) ⇒ युग्मन ⇒ **आंतरिक-कक्षीय, कम-स्पिन**।',
            },
            {
              english: 'Weak field ligand (F⁻, H₂O, Cl⁻) ⇒ no pairing ⇒ **outer-orbital, high-spin**.',
        hindi: 'कमजोर क्षेत्र लिगैंड (F⁻, H₂O, Cl⁻) ⇒ कोई युग्मन नहीं ⇒ **बाहरी-कक्षीय, उच्च-स्पिन**।',
            },
            {
              english: 'Examples:',
        hindi: 'उदाहरण:',
            },
            {
              english: '• $[Fe(CN)_6]^{4-}$ Fe²⁺ $d^6$ + CN⁻ strong → all paired → $d^2sp^3$, diamagnetic.',
        hindi: '• $[Fe(CN)_6]^{4-}$ Fe²⁺ $d^6$ + CN⁻ मजबूत → सभी युग्मित → $d^2sp^3$, प्रतिचुंबकीय।',
            },
            {
              english: '• $[Fe(H_2O)_6]^{2+}$ Fe²⁺ $d^6$ + H₂O weak → 4 unpaired → $sp^3d^2$, paramagnetic ($\\mu \\approx 4.9$).',
        hindi: '• $[Fe(H_2O)_6]^{2+}$ Fe²⁺ $d^6$ + H₂O कमजोर → 4 अयुग्मित → $sp^3d^2$, अनुचुंबकीय ($\\mu \\approx 4.9$)।',
            },
            {
              english: '• $[Ni(CN)_4]^{2-}$ Ni²⁺ $d^8$ + CN⁻ strong → pair $d^8$ → $dsp^2$ square planar, diamagnetic.',
        hindi: '• $[Ni(CN)_4]^{2-}$ Ni²⁺ $d^8$ + CN⁻ मजबूत → जोड़ी $d^8$ → $dsp^2$ वर्ग तलीय, प्रतिचुंबकीय।',
            },
            {
              english: '• $[NiCl_4]^{2-}$ → $sp^3$ tetrahedral, paramagnetic ($\\mu \\approx 2.83$).',
        hindi: '• $[NiCl_4]^{2-}$ → $sp^3$ टेट्राहेड्रल, पैरामैग्नेटिक ($\\mu \\approx 2.83$)।',
            },
          ],
        },
      ],
    },
    {
      id: 'cft',
      title: {
        english: 'Crystal Field Theory (CFT) & Spectrochemical Series',
        hindi: 'क्रिस्टल क्षेत्र सिद्धान्त (CFT) और स्पेक्ट्रोरासायनिक श्रेणी',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Electrostatic ligand-metal interaction; degenerate d-orbitals split:',
        hindi: 'इलेक्ट्रोस्टैटिक लिगैंड-मेटल इंटरेक्शन; पतित डी-कक्षकों का विभाजन:',
            },
            {
              english: '• Octahedral field: $t_{2g}$ (3 orbitals, lower by $0.4\\Delta_o$), $e_g$ (2 orbitals, higher by $0.6\\Delta_o$). Splitting $\\Delta_o$.',
        hindi: '• अष्टफलकीय क्षेत्र: $t_{2g}$ (3 कक्षक, $0.4\\Delta_o$ से कम), $e_g$ (2 कक्षक, $0.6\\Delta_o$ से अधिक)। बंटवारा $\\Delta_o$.',
            },
            {
              english: '• Tetrahedral field: $e$ (lower) and $t_2$ (higher); $\\Delta_t = (4/9)\\Delta_o$ — always weak field, **always high spin**.',
        hindi: '• टेट्राहेड्रल फ़ील्ड: $e$ (निचला) और $t_2$ (उच्चतर); $\\Delta_t = (4/9)\\Delta_o$ - हमेशा कमजोर क्षेत्र, **हमेशा उच्च स्पिन**।',
            },
            {
              english: 'Crystal Field Stabilisation Energy (CFSE) for octahedral:',
        hindi: 'अष्टफलकीय के लिए क्रिस्टल क्षेत्र स्थिरीकरण ऊर्जा (CFSE):',
            },
            {
              english: 'CFSE = $[-0.4\\,n(t_{2g}) + 0.6\\,n(e_g)]\\Delta_o + nP$ (P = pairing energy).',
        hindi: 'सीएफएसई = $[-0.4\\,n(t_{2g}) + 0.6\\,n(e_g)]\\Delta_o + nP$ (पी = युग्मन ऊर्जा)।',
            },
            {
              english: '**High vs low spin** in octahedral d⁴–d⁷: depends on $\\Delta_o$ vs $P$.',
        hindi: '**उच्च बनाम निम्न स्पिन** अष्टफलकीय d⁴–d⁷ में: $\\Delta_o$ बनाम $P$ पर निर्भर करता है।',
            },
            {
              english: '• $\\Delta_o > P$ → low spin (strong field).',
        hindi: '• $\\Delta_o > P$ → कम स्पिन (मजबूत क्षेत्र)।',
            },
            {
              english: '• $\\Delta_o < P$ → high spin (weak field).',
        hindi: '• $\\Delta_o < P$ → उच्च स्पिन (कमजोर क्षेत्र)।',
            },
            {
              english: '**Spectrochemical series** (increasing field strength):',
        hindi: '**स्पेक्ट्रोकेमिकल श्रृंखला** (क्षेत्र की ताकत बढ़ाना):',
            },
            {
              english: '$I^- < Br^- < S^{2-} < SCN^- < Cl^- < NO_3^- < F^- < OH^- < C_2O_4^{2-} < H_2O < NCS^- < edta < NH_3 < en < NO_2^- < CN^- < CO$.',
        hindi: '$I^- < Br^- < S^{2-} < SCN^- < Cl^- < NO_3^- < F^- < OH^- < C_2O_4^{2-} < H_2O < NCS^- < edta < NH_3 < en < NO_2^- < CN^- < CO$.',
            },
            {
              english: '**Colour** of complex is due to d-d transitions: complementary colour absorbed at energy $\\Delta_o$ ⇒ ion appears in remaining colour.',
        hindi: 'कॉम्प्लेक्स का **रंग** डी-डी संक्रमण के कारण होता है: ऊर्जा पर अवशोषित पूरक रंग $\\Delta_o$ ⇒ आयन शेष रंग में दिखाई देता है।',
            },
          ],
        },
      ],
    },
    {
      id: 'metal-carbonyls',
      title: {
        english: 'Metal Carbonyls & Bonding',
        hindi: 'धातु कार्बोनिल और बन्धन',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Examples: $[Ni(CO)_4]$ tetrahedral, $[Fe(CO)_5]$ TBP, $[Cr(CO)_6]$ octahedral, $[Mn_2(CO)_{10}]$ binuclear (Mn–Mn bond).',
        hindi: 'उदाहरण: $[Ni(CO)_4]$ चतुष्फलकीय, $[Fe(CO)_5]$ TBP, $[Cr(CO)_6]$ अष्टफलकीय, $[Mn_2(CO)_{10}]$ द्विनाभिक (Mn-Mn आबंध)।',
            },
            {
              english: '**M–CO bond**: synergic — (i) $\\sigma$ donation from filled lone pair on C to empty M hybrid orbital; (ii) **$\\pi$-back-bonding** from filled metal $d$ to empty CO $\\pi^*$. Strengthens M–C, weakens C≡O (IR shift to lower wavenumber).',
        hindi: '**एम-सीओ बांड**: सहक्रियात्मक - (i) $\\sigma$ C पर भरे हुए एकाकी जोड़े से खाली M संकर कक्षक तक दान; (ii) **$\\pi$-बैक-बॉन्डिंग** भरी हुई धातु $d$ से खाली CO $\\pi^*$ तक। एम-सी को मजबूत करता है, सी≡ओ को कमजोर करता है (आईआर निचली तरंग संख्या में शिफ्ट होता है)।',
            },
            {
              english: 'EAN (Effective Atomic Number) rule: stable carbonyls have EAN equal to nearest noble gas.',
        hindi: 'ईएएन (प्रभावी परमाणु संख्या) नियम: स्थिर कार्बोनिल में ईएएन निकटतम उत्कृष्ट गैस के बराबर होता है।',
            },
            {
              english: 'EAN = Z(metal) − O.S. + 2 × (number of donor electrons from ligands).',
        hindi: 'EAN = Z(metal) − O.S. + 2 × (लिगैंड्स से दाता इलेक्ट्रॉनों की संख्या)।',
            },
          ],
        },
      ],
    },
    {
      id: 'applications',
      title: {
        english: 'Stability, Importance & Applications',
        hindi: 'स्थायित्व, महत्व और अनुप्रयोग',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Stability constant: $\\beta = \\dfrac{[ML_n]}{[M][L]^n}$. Higher $\\beta$ ⇒ more stable.',
        hindi: 'स्थिरता स्थिरांक: $\\beta = \\dfrac{[ML_n]}{[M][L]^n}$. उच्चतर $\\beta$ ⇒ अधिक स्थिर।',
            },
            {
              english: '**Chelate effect** — bi/poly­dentate ligands give more stable complexes than equivalent monodentate (entropy-driven).',
        hindi: '**चेलेट प्रभाव** - bi/polyडेंटेट लिगेंड समकक्ष मोनोडेंटेट (एन्ट्रॉपी-चालित) की तुलना में अधिक स्थिर कॉम्प्लेक्स देते हैं।',
            },
            {
              english: 'Biological: chlorophyll (Mg-porphyrin), haemoglobin (Fe-porphyrin), vit B₁₂ (Co-corrin).',
        hindi: 'जैविक: क्लोरोफिल (एमजी-पोर्फिरिन), हीमोग्लोबिन (फे-पोर्फिरिन), विटामिन बी₁₂ (को-कोरिन)।',
            },
            {
              english: 'Analytical: EDTA titration for hardness; nickel detection by DMG (red ppt); Tollens $[Ag(NH_3)_2]^+$.',
        hindi: 'विश्लेषणात्मक: कठोरता के लिए EDTA अनुमापन; डीएमजी (लाल पीपीटी) द्वारा निकल का पता लगाना; टॉलेंस $[Ag(NH_3)_2]^+$.',
            },
            {
              english: 'Extraction: cyanide leaching of Au/Ag; Mond ($Ni(CO)_4$).',
        hindi: 'निष्कर्षण: Au/Ag का साइनाइड निक्षालन; मोंड ($Ni(CO)_4$).',
            },
            {
              english: 'Cisplatin $cis$-$[Pt(NH_3)_2Cl_2]$ — anti-cancer (trans is inactive).',
        hindi: 'सिस्प्लैटिन $cis$-$[Pt(NH_3)_2Cl_2]$ - कैंसर रोधी (ट्रांस निष्क्रिय है)।',
            },
          ],
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: '\\Delta_t = \\tfrac{4}{9}\\Delta_o',
      desc: {
        english: 'Tetrahedral splitting',
        hindi: 'चतुष्फलकीय विभाजन',
      },
    },
    {
      latex: '\\text{CFSE} = [-0.4\\,n_{t_{2g}} + 0.6\\,n_{e_g}]\\Delta_o + nP',
      desc: {
        english: 'Octahedral',
        hindi: 'अष्टफलकीय',
      },
    },
    {
      latex: 'I^- < \\dots < H_2O < NH_3 < en < CN^- < CO',
      desc: {
        english: 'Spectrochemical series',
        hindi: 'स्पेक्ट्रोकेमिकल श्रृंखला',
      },
    },
    {
      latex: '\\mu = \\sqrt{n(n+2)}\\ \\text{B.M.}',
      desc: {
        english: 'Magnetic moment',
        hindi: 'चुंबकीय क्षण',
      },
    },
    {
      latex: '\\beta = \\dfrac{[ML_n]}{[M][L]^n}',
      desc: {
        english: 'Overall stability constant',
        hindi: 'समग्र स्थिरता स्थिरांक',
      },
    },
    {
      latex: 'EAN = Z - O.S. + 2L',
      desc: {
        english: 'Effective atomic number',
        hindi: 'प्रभावी परमाणु संख्या',
      },
    },
    {
      latex: '[Ni(CN)_4]^{2-}\\ dsp^2,\\ [NiCl_4]^{2-}\\ sp^3',
      desc: {
        english: 'Geometry contrast',
        hindi: 'ज्यामिति कंट्रास्ट',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'JEE Main',
      tag: 'Hybridisation',
      problem: {
        english: 'Hybridisation of Ni in $[Ni(CO)_4]$:',
        hindi: '$[Ni(CO)_4]$ में Ni का संकरण:',
      },
      answer: {
        english: '$sp^3$',
        hindi: '$sp^3$',
      },
      solution: {
        english: 'Tetrahedral, diamagnetic (CO pairs Ni⁰ d-e⁻).',
        hindi: 'चतुष्फलकीय, प्रतिचुंबकीय (CO जोड़े Ni⁰ d-e⁻)।',
      },
    },
    {
      year: 2023,
      exam: 'NEET',
      tag: 'Isomerism',
      problem: {
        english: 'Number of geometrical isomers of $[Co(NH_3)_3Cl_3]$:',
        hindi: '$[Co(NH_3)_3Cl_3]$ के ज्यामितीय आइसोमर्स की संख्या:',
      },
      answer: {
        english: '2 (fac and mer)',
        hindi: '2 (fac और mer)',
      },
      solution: {
        english: 'Octahedral $MA_3B_3$.',
        hindi: 'अष्टफलकीय $MA_3B_3$।',
      },
    },
    {
      year: 2022,
      exam: 'JEE Main',
      tag: 'CFT',
      problem: {
        english: 'Spin-only $\\mu$ of $[Fe(CN)_6]^{3-}$:',
        hindi: '$[Fe(CN)_6]^{3-}$ में से केवल $\\mu$ स्पिन:',
      },
      answer: {
        english: '1.73 B.M.',
        hindi: '1.73 बी.एम.',
      },
      solution: {
        english: 'Fe³⁺ $d^5$, low spin (CN⁻ strong) → 1 unpaired.',
        hindi: 'Fe³⁺ $d^5$, कम स्पिन (CN⁻ मजबूत) → 1 अयुग्मित।',
      },
    },
    {
      year: 2021,
      exam: 'NEET',
      tag: 'Nomenclature',
      problem: {
        english: 'IUPAC name of $K_3[Fe(C_2O_4)_3]$:',
        hindi: '$K_3[Fe(C_2O_4)_3]$ का IUPAC नाम:',
      },
      answer: {
        english: 'Potassium trioxalato­ferrate(III)',
        hindi: 'पोटेशियम ट्राइऑक्सालेटोफेरेट(III)',
      },
      solution: {
        english: 'Anionic complex → -ate.',
        hindi: 'अनियोनिक कॉम्प्लेक्स → -एट।',
      },
    },
    {
      year: 2020,
      exam: 'JEE Main',
      tag: 'Optical',
      problem: {
        english: 'Which is optically active?',
        hindi: 'कौन सा प्रकाशिक रूप से सक्रिय है?',
      },
      answer: {
        english: 'cis-$[CoCl_2(en)_2]^+$',
        hindi: 'सिस-$[CoCl_2(en)_2]^+$',
      },
      solution: {
        english: 'No plane of symmetry; trans has σ.',
        hindi: 'समरूपता का कोई तल नहीं; ट्रांस में σ है।',
      },
    },
  ],
  next: {
    slug: 'haloalkanes-haloarenes',
    title: 'Haloalkanes & Haloarenes',
  },
};

export default chapter;
